/**
 * Multi-Leg Pricing — which origin a leg's regional rate is matched against,
 * stored on the contract as `originAttributionScope`.
 *
 * `leg` (the API default) matches every leg on its own region
 * pair. `serviceContinuityRun` holds the trip's originating region constant
 * across the run, so a return leg keeps the rate that carried the vehicle
 * out — which is what removes the need for "any-to-any" rate rules.
 */
export const ORIGIN_ATTRIBUTION_SCOPES = Object.freeze({
  PER_LEG: 'leg',
  TRIP_ORIGIN_REGION: 'serviceContinuityRun',
});

/**
 * Section heading and dropdown label, and the Contract Information card's row
 * label.
 */
export const MULTI_LEG_PRICING_LABEL = 'Multi-Leg Pricing';

/**
 * Read for a saved contract with no scope, or one this UI doesn't know. The
 * API prices those per-leg, so the form and card must too.
 */
export const DEFAULT_ORIGIN_ATTRIBUTION_SCOPE =
  ORIGIN_ATTRIBUTION_SCOPES.PER_LEG;

/** Preselected when adding a provider, whose contract has no scope yet. */
export const NEW_CONTRACT_ORIGIN_ATTRIBUTION_SCOPE =
  ORIGIN_ATTRIBUTION_SCOPES.TRIP_ORIGIN_REGION;

/**
 * The two options. `description` carries the full behaviour including the
 * fallback rule, and is the only tier of copy: it shows as the dropdown's
 * caption for the selected option, while the option rows show the label alone.
 */
export const ORIGIN_ATTRIBUTION_SCOPE_OPTIONS = [
  {
    value: ORIGIN_ATTRIBUTION_SCOPES.PER_LEG,
    label: 'Price each leg on its own',
    description:
      "Each leg is priced with the rates for its own pickup and drop-off. If no rate covers that leg, it uses the first leg's rates.",
  },
  {
    value: ORIGIN_ATTRIBUTION_SCOPES.TRIP_ORIGIN_REGION,
    label: 'Price from where the trip started',
    description:
      "Every leg is priced as if it picked up where the trip started (the first leg's pickup). A leg returning to the starting area uses the same rates as the way out. If no rate covers a leg from the starting area, it uses the first leg's rates.",
  },
];

/**
 * Resolves a stored scope to its dropdown option, falling back to the default
 * for a missing value or one this UI doesn't know about.
 * @param {string|null|undefined} scope
 * @returns {Object} The matching option
 */
export const getOriginAttributionScopeOption = (scope) =>
  ORIGIN_ATTRIBUTION_SCOPE_OPTIONS.find((opt) => opt.value === scope) ??
  ORIGIN_ATTRIBUTION_SCOPE_OPTIONS.find(
    (opt) => opt.value === DEFAULT_ORIGIN_ATTRIBUTION_SCOPE,
  );

/**
 * Normalizes an arbitrary value to a scope the API will accept.
 * @param {string|null|undefined} scope
 * @returns {string} `'leg'` or `'serviceContinuityRun'`
 */
export const normalizeOriginAttributionScope = (scope) =>
  Object.values(ORIGIN_ATTRIBUTION_SCOPES).includes(scope)
    ? scope
    : DEFAULT_ORIGIN_ATTRIBUTION_SCOPE;

/** Human label for a stored scope — used by the Contract Information card. */
export const getOriginAttributionScopeLabel = (scope) =>
  getOriginAttributionScopeOption(scope).label;
